import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { PageHeader, Loader } from "@/components/shared";
import { formatINR, formatDate, formatTimeIST, localTz } from "@/lib/format";
import { Check, ArrowLeft, Video, MapPin, Clock } from "lucide-react";

const STEPS = ["Service", "Date & time", "Your details", "Confirm"];

export default function Book() {
  useSeo({
    title: "Book a Family Constellation Session — Ludhiana or Online",
    description: "Book a 1:1 Family Constellation session with Arjunna Soni in Ludhiana or online. Choose a service, pick a time, and reserve your place in under four minutes.",
    path: "/book",
  });
  // Privacy: pause PostHog session recording during booking.
  useEffect(() => {
    try { window.posthog?.stopSessionRecording?.(); } catch {}
    return () => { try { window.posthog?.startSessionRecording?.(); } catch {} };
  }, []);
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [services, setServices] = useState(null);
  const [service, setService] = useState(null);
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", city: "", concernSummary: "", referralSource: "", isReturningClient: false, consent: false });
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => {
      const bookable = r.data.filter((s) => s.isBookable);
      setServices(bookable);
      const pre = params.get("service");
      if (pre) {
        const found = bookable.find((s) => s.slug === pre);
        if (found) { setService(found); setStep(1); }
      }
    }).catch(() => setServices([]));
  }, [params]);

  useEffect(() => {
    if (service && step === 1) {
      setSlots(null);
      api.get("/availability", { params: { serviceId: service.service_id } })
        .then((r) => setSlots(r.data)).catch(() => setSlots([]));
    }
  }, [service, step]);

  const tz = useMemo(() => localTz(), []);

  const chooseService = (s) => { setService(s); setSlot(null); setStep(1); };

  const submit = async () => {
    if (!form.consent) { toast.error("Please accept the consent checkboxes."); return; }
    setBusy(true);
    try {
      const res = await api.post("/bookings", {
        serviceId: service.service_id, scheduledStart: slot.start, ...form,
      });
      setConfirmed(res.data);
      setStep(4);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please pick another time.");
    } finally { setBusy(false); }
  };

  if (confirmed) {
    return (
      <div>
        <section className="container-page py-16 md:py-24 max-w-xl text-center" data-testid="booking-confirmation">
          <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-5"><Check className="text-gold-dark" size={30} /></div>
          <h1 className="text-3xl md:text-4xl text-earth mb-3" style={{ fontWeight: 600 }}>Your session is requested</h1>
          <p className="text-earth/70 mb-6">Arjunna will confirm your booking shortly. Please keep your reference safe.</p>
          <div className="bg-surface-card border hairline rounded-lg p-6 text-left space-y-2 mb-8">
            <div className="flex justify-between"><span className="text-earth/60">Reference</span><span className="font-semibold text-earth">{confirmed.bookingReference}</span></div>
            <div className="flex justify-between"><span className="text-earth/60">Service</span><span className="font-semibold text-earth">{confirmed.serviceTitle}</span></div>
            <div className="flex justify-between"><span className="text-earth/60">When</span><span className="font-semibold text-earth">{formatDate(confirmed.scheduledStart)}, {formatTimeIST(confirmed.scheduledStart)} IST</span></div>
            <div className="flex justify-between"><span className="text-earth/60">Fee</span><span className="font-semibold text-earth">{formatINR(confirmed.price)}</span></div>
          </div>
          <p className="text-xs text-earth/50 mb-6">No payment has been taken. Payment details will be shared at confirmation.</p>
          <Link to="/" className="btn-gold">Back home</Link>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHeader crumb="Book" title="Book a session" />
      <section className="container-page pb-16 md:pb-24 max-w-3xl">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${i <= step ? "text-earth" : "text-earth/30"}`}>
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${i < step ? "bg-gold text-earth" : i === step ? "bg-earth text-parchment" : "border hairline"}`}>{i < step ? <Check size={14} /> : i + 1}</span>
                <span className="hidden sm:inline text-sm font-semibold">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-gold" : "bg-hairline"}`} />}
            </React.Fragment>
          ))}
        </div>

        {step > 0 && step < 4 && (
          <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 label-caps text-earth/50 hover:text-earth mb-6" data-testid="booking-back"><ArrowLeft size={14} /> Back</button>
        )}

        {/* STEP 0: SERVICE */}
        {step === 0 && (
          <div data-testid="booking-step-service">
            <h2 className="text-2xl text-earth mb-6" style={{ fontWeight: 600 }}>Which session would you like?</h2>
            {!services ? <Loader /> : (
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((s) => (
                  <button key={s.service_id} onClick={() => chooseService(s)} className="text-left bg-surface-card border hairline rounded-lg p-6 hover:border-gold transition-all" data-testid={`book-service-${s.slug}`}>
                    <div className="flex items-center gap-2 label-caps text-earth/60 mb-2">{s.format.includes("Online") ? <Video size={14} /> : <MapPin size={14} />} {s.format}</div>
                    <h3 className="text-xl text-earth mb-1">{s.title}</h3>
                    <div className="flex items-center gap-2 text-sm text-earth/60 mb-3"><Clock size={14} /> {s.durationLabel}</div>
                    <div className="text-2xl text-earth font-display" style={{ fontWeight: 600 }}>{formatINR(s.price)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 1: DATE & TIME */}
        {step === 1 && (
          <div data-testid="booking-step-datetime">
            <h2 className="text-2xl text-earth mb-2" style={{ fontWeight: 600 }}>Choose a time</h2>
            <p className="text-earth/60 mb-6">All times shown in IST. {service?.format?.includes("Online") && <span>Your local timezone: {tz}.</span>}</p>
            {!slots ? <Loader /> : slots.length === 0 ? (
              <p className="text-earth/60 py-8">No slots available in the next 30 days. Please <Link to="/contact" className="text-gold-dark underline">contact us</Link> to arrange a time.</p>
            ) : (
              <div className="space-y-6">
                {slots.map((day) => (
                  <div key={day.date}>
                    <div className="label-caps text-earth/60 mb-3">{formatDate(day.date, { weekday: "long" })}</div>
                    <div className="flex flex-wrap gap-2">
                      {day.slots.map((sl) => (
                        <button key={sl.start} disabled={!sl.available} onClick={() => { setSlot(sl); setStep(2); }}
                          className={`px-4 py-2.5 rounded-md text-sm font-semibold border transition-all ${sl.available ? "bg-surface-card hairline text-earth hover:border-gold hover:bg-gold/10" : "opacity-40 cursor-not-allowed line-through hairline"}`}
                          data-testid={`slot-${sl.start}`}>
                          {formatTimeIST(sl.start)}
                          {service?.format?.includes("Online") && sl.available && <span className="block text-[10px] text-earth/50 font-normal">{new Date(sl.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} local</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 2: DETAILS */}
        {step === 2 && (
          <div data-testid="booking-step-details">
            <h2 className="text-2xl text-earth mb-6" style={{ fontWeight: 600 }}>Your details</h2>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Full name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} className="px-4 py-3 rounded-md bg-surface-card border hairline outline-none focus:border-gold" data-testid="book-name" />
                <input required placeholder="Phone (+91…)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-4 py-3 rounded-md bg-surface-card border hairline outline-none focus:border-gold" data-testid="book-phone" />
              </div>
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-md bg-surface-card border hairline outline-none focus:border-gold" data-testid="book-email" />
              <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full px-4 py-3 rounded-md bg-surface-card border hairline outline-none focus:border-gold" data-testid="book-city" />
              <div>
                <label className="label-caps text-earth/60 block mb-2">What would you like to work with? <span className="normal-case font-normal">(optional)</span></label>
                <textarea maxLength={1000} rows={4} value={form.concernSummary} onChange={(e) => setForm({ ...form, concernSummary: e.target.value })} className="w-full px-4 py-3 rounded-md bg-surface-card border hairline outline-none focus:border-gold" data-testid="book-concern" />
                <p className="text-xs text-earth/50 mt-1">This is confidential and seen only by Arjunna.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <select value={form.referralSource} onChange={(e) => setForm({ ...form, referralSource: e.target.value })} className="px-4 py-3 rounded-md bg-surface-card border hairline outline-none focus:border-gold" data-testid="book-referral">
                  <option value="">How did you find me?</option><option>Instagram</option><option>Google search</option><option>A friend</option><option>Other</option>
                </select>
                <label className="flex items-center gap-2 text-earth/80"><input type="checkbox" checked={form.isReturningClient} onChange={(e) => setForm({ ...form, isReturningClient: e.target.checked })} className="accent-[#C9A84C]" data-testid="book-returning" /> I've worked with Arjunna before</label>
              </div>
              <button onClick={() => { if (!form.fullName || !form.email || !form.phone) { toast.error("Please fill in your name, email and phone."); return; } setStep(3); }} className="btn-gold" data-testid="book-continue">Continue</button>
            </div>
          </div>
        )}

        {/* STEP 3: CONFIRM */}
        {step === 3 && (
          <div data-testid="booking-step-confirm">
            <h2 className="text-2xl text-earth mb-6" style={{ fontWeight: 600 }}>Review & confirm</h2>
            <div className="bg-surface-card border hairline rounded-lg p-6 space-y-2 mb-6">
              <div className="flex justify-between"><span className="text-earth/60">Service</span><span className="font-semibold text-earth">{service?.title}</span></div>
              <div className="flex justify-between"><span className="text-earth/60">When</span><span className="font-semibold text-earth">{formatDate(slot?.start)}, {formatTimeIST(slot?.start)} IST</span></div>
              <div className="flex justify-between"><span className="text-earth/60">Name</span><span className="font-semibold text-earth">{form.fullName}</span></div>
              <div className="flex justify-between border-t hairline pt-2 mt-2"><span className="text-earth/60">Fee</span><span className="font-semibold text-earth">{formatINR(service?.price)}</span></div>
            </div>
            <label className="flex items-start gap-2 text-sm text-earth/75 cursor-pointer mb-5">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-[#C9A84C]" data-testid="book-consent" />
              <span>I have read and accept the <Link to="/terms" className="text-gold-dark underline">terms & cancellation policy</Link>, the <Link to="/disclaimer" className="text-gold-dark underline">scope-of-practice disclaimer</Link>, and the <Link to="/privacy-policy" className="text-gold-dark underline">privacy policy</Link>.</span>
            </label>
            <button onClick={submit} disabled={busy} className="btn-gold w-full sm:w-auto" data-testid="book-submit">{busy ? "Requesting…" : "Request this session"}</button>
            <p className="text-xs text-earth/50 mt-3">No payment is taken online right now. Arjunna will confirm and share payment details.</p>
          </div>
        )}
      </section>
    </div>
  );
}
